import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  AlertCircle,
  ArrowLeft,
  Share2,
  Pencil,
  Send,
  Users,
  Clock,
  BarChart3,
  Eye,
  Lock,
  PlayCircle,
  RotateCcw,
  FlaskConical,
  CheckCircle2,
} from 'lucide-react';
import api, { MarketplaceArcheDetail } from '@/lib/api';
import { usePoll, usePollResults } from '../hooks';
import { PollStatusBadge, PollQuestionView, PollResultsView } from '../components';
import type { PollAnswer, PollQuestion } from '../types';
import { shareOrigin } from '@/lib/portal-host';

/**
 * Mirrors the server-side `validateSubmitPollResponseInput` invariant
 * (required questions must have a selection or non-empty text). Kept
 * inline here because the server validator lives in @fias/db-types
 * which the client can't import. Test Mode never hits the server, so
 * it needs to know the rules locally to give the creator a faithful
 * preview of what a real respondent would see.
 */
function validateAnswersLocally(
  questions: PollQuestion[],
  answers: Record<string, PollAnswer>,
): { valid: true } | { valid: false; missing: string[] } {
  const missing: string[] = [];
  for (const q of questions) {
    if (!q.required) continue;
    const a = answers[q.questionId];
    const hasText = !!a?.textAnswer && a.textAnswer.trim().length > 0;
    const hasChoice = !!a?.selectedOptions && a.selectedOptions.length > 0;
    if (q.type === 'free_text' ? !hasText : !hasChoice) {
      missing.push(q.text);
    }
  }
  return missing.length === 0 ? { valid: true } : { valid: false, missing };
}

const POLLS_ARCHE_ID = 'arche_polls';
const PUBLISH_ACTION = 'publish_poll';

function BackToPollsLink() {
  return (
    <Link
      to="/a/arche_polls"
      className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to Polls
    </Link>
  );
}

export function PollPage() {
  const { pollId } = useParams<{ pollId: string }>();
  const navigate = useNavigate();
  const { poll, loading, error, refresh } = usePoll(pollId);
  const {
    results,
    loading: resultsLoading,
    error: resultsError,
    refresh: refreshResults,
  } = usePollResults(
    poll && (poll.isCreator || (poll.hasResponded && poll.resultsVisibility === 'public'))
      ? pollId
      : undefined,
  );

  const [answers, setAnswers] = useState<Record<string, PollAnswer>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [publishCostCredits, setPublishCostCredits] = useState<number | null>(null);

  // Publish confirmation — gates the irreversible publish step behind
  // an explicit ack so the creator can't accidentally lock the poll's
  // structure (and burn credits) with a stray click.
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Test mode (draft only): a local-only dry run so the creator can
  // verify the form behaves correctly before flipping the poll live.
  const [testAnswers, setTestAnswers] = useState<Record<string, PollAnswer>>({});
  const [testResult, setTestResult] = useState<
    { ok: true } | { ok: false; missing: string[] } | null
  >(null);

  useEffect(() => {
    api
      .getMarketplaceArche(POLLS_ARCHE_ID)
      .then((arche: MarketplaceArcheDetail) => {
        const price = arche.actionPricing?.[PUBLISH_ACTION];
        if (price) setPublishCostCredits(price.costCredits);
      })
      .catch(() => {
        /* non-critical */
      });
  }, []);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-4 pt-6 px-4 sm:px-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-48 w-full rounded-lg" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    );
  }

  if (error || !poll) {
    return (
      <div className="max-w-3xl mx-auto pt-6 px-4 sm:px-6">
        <BackToPollsLink />
        <Card className="border-destructive/50">
          <CardContent className="flex items-center gap-3 pt-6">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
            <p className="text-muted-foreground">{error || 'Poll not found'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const canVote = poll.status === 'active' && !poll.hasResponded && !poll.isCreator;
  const showResults = poll.isCreator || (poll.hasResponded && poll.resultsVisibility === 'public');

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);

    const answersList = poll.questions.map((q) => {
      const a = answers[q.questionId];
      if (!a) {
        return { questionId: q.questionId, selectedOptions: [], textAnswer: '' };
      }
      return a;
    });

    try {
      await api.submitPollResponse(pollId!, { answers: answersList });
      refresh();
      refreshResults();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAction = async (action: 'publish' | 'close' | 'reopen') => {
    setActionError(null);
    try {
      if (action === 'publish') {
        setPublishing(true);
        await api.publishPoll(pollId!);
      } else if (action === 'close') {
        await api.closePoll(pollId!);
      } else {
        await api.reopenPoll(pollId!);
      }
      setPublishConfirmOpen(false);
      refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to ${action} poll`;
      if (message.includes('Insufficient credits')) {
        setActionError('Insufficient credits to publish this poll. Please add more credits.');
      } else {
        setActionError(message);
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleTestSubmit = () => {
    if (!poll) return;
    const result = validateAnswersLocally(poll.questions, testAnswers);
    setTestResult(result.valid ? { ok: true } : { ok: false, missing: result.missing });
  };

  const handleTestReset = () => {
    setTestAnswers({});
    setTestResult(null);
  };

  const handleCopyLink = async () => {
    const url = `${shareOrigin()}/a/arche_polls/${poll.pollId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="max-w-3xl mx-auto pt-6 px-4 sm:px-6">
      <BackToPollsLink />
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 mb-2">
              <h1 className="text-2xl font-bold">{poll.title}</h1>
              <PollStatusBadge status={poll.status} />
            </div>
            {poll.description && (
              <p className="text-muted-foreground leading-relaxed">{poll.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-5 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" />
            {poll.responseCount} {poll.responseCount === 1 ? 'response' : 'responses'}
          </span>
          {poll.closesAt && (
            <span className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {new Date(poll.closesAt) > new Date() ? 'Closes' : 'Closed'}{' '}
              {new Date(poll.closesAt).toLocaleString()}
            </span>
          )}
        </div>

        {/* Creator actions */}
        {poll.isCreator && (
          <div className="flex items-center gap-2 mt-4">
            {poll.status === 'draft' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/a/arche_polls/${poll.pollId}/edit`)}
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />
                  Edit
                </Button>
                <Button size="sm" onClick={() => setPublishConfirmOpen(true)}>
                  <Send className="h-3.5 w-3.5 mr-1.5" />
                  {publishCostCredits ? `Publish (${publishCostCredits} credits)` : 'Publish'}
                </Button>
              </>
            )}
            {poll.status === 'active' && (
              <>
                <Button variant="outline" size="sm" onClick={handleCopyLink}>
                  <Share2 className="h-3.5 w-3.5 mr-1.5" />
                  {copied ? 'Copied!' : 'Share Link'}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => handleAction('close')}>
                  Close Poll
                </Button>
              </>
            )}
            {poll.status === 'closed' && (
              <Button size="sm" onClick={() => handleAction('reopen')}>
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                Reopen Poll
              </Button>
            )}
          </div>
        )}

        {/* Locked-after-publish banner — visible to the creator on
            active and closed polls so the absence of an Edit button
            is explained rather than mysterious. */}
        {poll.isCreator && (poll.status === 'active' || poll.status === 'closed') && (
          <div className="mt-4 p-3 rounded-lg border border-border bg-muted/30 text-sm flex items-start gap-2">
            <Lock className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
            <div>
              <p className="font-medium text-foreground">
                {poll.status === 'active'
                  ? 'Poll is live — questions are locked.'
                  : 'Poll is closed — questions are locked.'}
              </p>
              <p className="text-muted-foreground text-xs mt-0.5">
                {poll.status === 'active'
                  ? 'You can close it any time, but you cannot edit questions or options while responses are coming in. Existing responses will be preserved if you close.'
                  : 'You can reopen it to accept new responses, but the questions cannot be changed. Existing responses are preserved.'}
              </p>
            </div>
          </div>
        )}
      </div>

      {actionError && (
        <div className="mb-6 p-3 bg-destructive/10 text-destructive rounded-lg text-sm flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {actionError}
        </div>
      )}

      {/* Voting form */}
      {canVote && (
        <div className="space-y-3 mb-8">
          {poll.questions.map((q) => (
            <PollQuestionView
              key={q.questionId}
              question={q}
              answer={answers[q.questionId] || { questionId: q.questionId }}
              onChange={(a) => setAnswers((prev) => ({ ...prev, [q.questionId]: a }))}
            />
          ))}

          {submitError && (
            <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {submitError}
            </div>
          )}

          <div className="pt-2">
            <Button onClick={handleSubmit} disabled={submitting}>
              <Send className="h-4 w-4 mr-2" />
              {submitting ? 'Submitting...' : 'Submit Response'}
            </Button>
          </div>
        </div>
      )}

      {/* Already responded message */}
      {poll.hasResponded && !poll.isCreator && poll.resultsVisibility === 'creator_only' && (
        <Card className="mb-6">
          <CardContent className="flex items-center gap-3 pt-6">
            <Eye className="h-5 w-5 text-muted-foreground shrink-0" />
            <p className="text-sm text-muted-foreground">
              You have already responded to this poll. Results are only visible to the poll creator.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {showResults && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Results
          </h2>
          {resultsLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-32 w-full rounded-lg" />
              <Skeleton className="h-32 w-full rounded-lg" />
            </div>
          ) : resultsError ? (
            <p className="text-destructive text-sm">{resultsError}</p>
          ) : results ? (
            <PollResultsView results={results} />
          ) : null}
        </div>
      )}

      {/* Draft: interactive Test Mode — renders the same voting form a
          respondent will see, validates locally on submit, never hits
          the server. Lets the creator catch typos / missed options /
          confused wording before flipping the poll live. */}
      {poll.isCreator && poll.status === 'draft' && (
        <div className="mb-8">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <FlaskConical className="h-5 w-5" />
              Test Mode
            </h2>
            <span className="text-xs text-muted-foreground">
              Try the poll as a respondent — nothing is saved.
            </span>
          </div>
          <div className="space-y-3">
            {poll.questions.map((q) => (
              <PollQuestionView
                key={q.questionId}
                question={q}
                answer={testAnswers[q.questionId] || { questionId: q.questionId }}
                onChange={(a) => {
                  setTestAnswers((prev) => ({ ...prev, [q.questionId]: a }));
                  setTestResult(null);
                }}
              />
            ))}
          </div>

          {testResult && (
            <div
              className={`mt-4 p-3 rounded-lg text-sm flex items-start gap-2 ${
                testResult.ok
                  ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-900 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-900'
                  : 'bg-destructive/10 text-destructive border border-destructive/20'
              }`}
            >
              {testResult.ok ? (
                <>
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">Test submission would be accepted.</p>
                    <p className="text-xs mt-0.5">
                      All required questions are answered. Nothing was saved — publish the poll when
                      ready.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">
                      Required {testResult.missing.length === 1 ? 'question' : 'questions'} not
                      answered:
                    </p>
                    <ul className="list-disc list-inside text-xs mt-1 space-y-0.5">
                      {testResult.missing.map((t) => (
                        <li key={t} className="truncate">
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 mt-4">
            <Button onClick={handleTestSubmit}>
              <PlayCircle className="h-4 w-4 mr-2" />
              Test Submit
            </Button>
            <Button variant="outline" onClick={handleTestReset}>
              Reset
            </Button>
            <span className="ml-auto text-xs text-muted-foreground hidden sm:inline-flex items-center gap-1">
              <Eye className="h-3 w-3" />
              Preview only — no responses saved
            </span>
          </div>
        </div>
      )}

      {/* Publish confirmation — irreversible, charges credits if priced,
          and locks the question structure for the lifetime of the poll. */}
      <AlertDialog open={publishConfirmOpen} onOpenChange={setPublishConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish this poll?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Once published, the poll will start accepting responses immediately. You
                  <strong className="text-foreground">
                    {' '}
                    cannot edit the questions or options{' '}
                  </strong>
                  after publishing — only the creator can close (and later reopen) the poll.
                </p>
                {publishCostCredits ? (
                  <p>
                    This will charge{' '}
                    <strong className="text-foreground">{publishCostCredits} credits</strong> to
                    your account.
                  </p>
                ) : null}
                <p className="text-muted-foreground">
                  If you still want to tweak wording or options, cancel and use Test Mode to preview
                  as a respondent first.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={publishing}>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              disabled={publishing}
              onClick={(e) => {
                e.preventDefault();
                handleAction('publish');
              }}
            >
              {publishing ? 'Publishing…' : 'Publish poll'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
